<template>
  <div>
    <vue-element-loading
      :active="loading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#005f32"
      spinner="bar-fade-scale"
    />
    <v-snackbar v-model="snackbar" :color="snackbarColor" right bottom>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>

    <v-layout wrap>
      <v-flex xs12>
        <v-card-title class="elevation-0 px-0">
          <v-layout wrap align-center>
            <v-flex xs12 sm6>
              <span class="itemHeading" style="color: #005f32">My Troubleshooting Tickets</span>
            </v-flex>
            <v-flex xs12 sm6 text-right mt-2 mt-sm-0>
              <v-btn
                color="#005f32"
                dark
                class="rounded-xl px-6 kumbhBold white--text"
                to="/Troubleshoot/Report"
              >
                <v-icon left color="white">mdi-plus</v-icon>
                Report New Issue
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-title>

        <v-layout wrap justify-start mt-4>
          <v-flex xs12>
            <v-card class="elevation-2 rounded-lg overflow-hidden">
              <v-data-table
                :headers="headers"
                :items="tickets"
                :loading="loading"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="elevation-0 custom-ticket-table"
                @page-count="pageCount = $event"
                no-data-text="No tickets found"
              >
                <!-- Attendance Date Custom Slot (Commented out for now) -->
                <!-- <template v-slot:item.attendanceDate="{ item }">
                  <span v-if="item.attendanceDate" class="kumbhRegular">
                    {{ formatDate(item.attendanceDate) }}
                  </span>
                  <span v-else class="grey--text">General Bug</span>
                </template> -->

                <!-- Status Custom Slot -->
                <template v-slot:item.status="{ item }">
                  <v-chip
                    small
                    :color="getStatusColor(item.status)"
                    dark
                    class="kumbhBold px-4"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <!-- Created Date Custom Slot -->
                <template v-slot:item.create_date="{ item }">
                    <span class="kumbhRegular">
                        {{ formatDateTime(item.create_date) }}
                    </span>
                </template>

                <!-- Actions Slot -->
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    color="#005f32"
                    @click="viewTicketDetails(item)"
                    title="View Details"
                  >
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>

              <!-- Custom Pagination -->
              <v-layout wrap justify-center py-6 v-if="tickets.length > 0">
                <v-pagination
                  v-model="page"
                  :length="pageCount"
                  :total-visible="7"
                  class="custom-pagination"
                  color="#005f32"
                ></v-pagination>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- Ticket Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px" scrollable>
      <v-card v-if="selectedTicket" class="rounded-lg">
        <v-toolbar color="#005f32" dark flat dense>
          <v-toolbar-title class="kumbhBold">Ticket Details</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-layout wrap>
            <!-- Status and Reported On Row (Unified & Styled) -->
            <v-flex xs12 mb-3>
              <v-card flat class="pa-2 rounded-lg" style="background: #f1f8f5; border: 1px solid #e1eee7;">
                <v-layout align-center wrap>
                  <v-flex xs6>
                    <v-icon small color="#005f32" class="mr-1">mdi-shield-check-outline</v-icon>
                    <span class="grey--text text--darken-3 kumbhBold mr-2" style="font-size: 11px; text-transform: uppercase;">Status:</span>
                    <v-chip x-small :color="getStatusColor(selectedTicket.status)" dark class="kumbhBold px-3 elevation-1">
                      {{ selectedTicket.status }}
                    </v-chip>
                  </v-flex>
                  <v-flex xs6 text-right>
                    <v-icon small color="grey darken-1" class="mr-1">mdi-clock-outline</v-icon>
                    <span class="grey--text text--darken-3 kumbhBold mr-1" style="font-size: 11px; text-transform: uppercase;">Reported:</span>
                    <span class="kumbhMedium" style="font-size: 12px; color: #333;">{{ formatDateTime(selectedTicket.create_date) }}</span>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>

            <!-- Description with Icon and Styled Box -->
            <v-flex xs12 mb-3>
              <div class="d-flex align-center mb-2">
                <v-icon small color="#005f32" class="mr-2">mdi-text-box-outline</v-icon>
                <span class="grey--text text--darken-3 kumbhBold" style="font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase;">Issue Description</span>
              </div>
              <div class="kumbhRegular pa-3 rounded-lg white" style="white-space: pre-wrap; font-size: 14px; border: 1px solid #eee; box-shadow: 0 2px 4px rgba(0,0,0,0.02); color: #444; line-height: 1.5;">
                {{ selectedTicket.description }}
              </div>
            </v-flex>

            <!-- Admin Remark with Distinct Styling -->
            <v-flex xs12 mb-3 v-if="selectedTicket.adminRemark">
              <div class="d-flex align-center mb-2">
                <v-icon small color="blue darken-2" class="mr-2">mdi-comment-text-outline</v-icon>
                <span class="grey--text text--darken-3 kumbhBold" style="font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase;">Admin Resolution Note</span>
              </div>
              <div class="kumbhRegular blue-grey lighten-5 pa-3 rounded-lg" style="font-size: 14px; border-left: 4px solid #005f32; color: #2c3e50;">
                {{ selectedTicket.adminRemark }}
              </div>
            </v-flex>

            <!-- Screenshots Gallery -->
            <v-flex xs12 v-if="selectedTicket.screenshots && selectedTicket.screenshots.length > 0">
              <v-divider class="mb-3"></v-divider>
              <div class="d-flex align-center mb-2">
                <v-icon small color="#005f32" class="mr-2">mdi-image-multiple-outline</v-icon>
                <span class="grey--text text--darken-3 kumbhBold" style="font-size: 12px; letter-spacing: 0.5px; text-transform: uppercase;">Screenshots</span>
              </div>
              <v-layout wrap>
                <v-flex v-for="(img, idx) in selectedTicket.screenshots" :key="idx" xs3 pa-1>
                  <v-hover v-slot="{ hover }">
                    <v-card flat outlined class="rounded-lg overflow-hidden cursor-pointer transition-swing" 
                            :class="hover ? 'elevation-4' : 'elevation-0'"
                            @click="openImage(img)" 
                            style="border: 1px solid #ddd;">
                      <v-img :src="mediaURL + img" aspect-ratio="1" cover>
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="#005f32" size="20"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </v-card>
                  </v-hover>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn color="#005f32" dark class="px-6 rounded-lg kumbhBold" @click="detailsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "MyTickets",
  data() {
    return {
      loading: false,
      tickets: [],
      page: 1,
      itemsPerPage: 10,
      pageCount: 0,
      selectedTicket: null,
      detailsDialog: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      headers: [
        { text: "Ticket ID", value: "_id", class: "white--text font-weight-bold", width: "150" },
        { text: "Description", value: "description", class: "white--text font-weight-bold", truncate: true },
        // { text: "Attendance Date", value: "attendanceDate", class: "white--text font-weight-bold" },
        { text: "Status", value: "status", class: "white--text font-weight-bold" },
        { text: "Reported On", value: "create_date", class: "white--text font-weight-bold" },
        { text: "Actions", value: "actions", sortable: false, class: "white--text font-weight-bold", align: "right" },
      ],
    };
  },
  mounted() {
    this.getMyTickets();
  },
  methods: {
    async getMyTickets() {
      this.loading = true;
      try {
        const response = await axios({
          method: "GET",
          url: "/troubleshoot/my-tickets",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        });

        if (response.data.status) {
          this.tickets = response.data.data || [];
        } else {
          this.showSnackbar(response.data.msg || "Failed to fetch tickets", "error");
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
        this.showSnackbar("Unable to load troubleshooting tickets", "error");
      } finally {
        this.loading = false;
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "Open": return "blue";
        case "Under Review": return "orange";
        case "Resolved": return "green";
        case "Rejected": return "red";
        default: return "grey";
      }
    },
    formatDate(date) {
        if (!date) return "";
        return moment(date).format("DD/MM/YYYY");
    },
    formatDateTime(date) {
        if (!date) return "";
        return moment(date).utcOffset("+05:30").format("DD/MM/YYYY, HH:mm:ss");
    },
    viewTicketDetails(ticket) {
      this.selectedTicket = ticket;
      this.detailsDialog = true;
    },
    openImage(key) {
      window.open(this.mediaURL + key, "_blank");
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color === "success" ? "#005f32" : "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.itemHeading {
  font-family: 'KumbhBold';
  font-size: 24px;
}
.kumbhRegular {
  font-family: 'KumbhRegular';
}
.kumbhBold {
  font-family: 'KumbhBold';
}

.custom-ticket-table >>> thead {
  background-color: #005f32 !important;
}

.custom-ticket-table >>> th {
  font-family: "kumbhMedium" !important;
  font-size: 15px !important;
  color: #ffffff !important;
}

.custom-ticket-table >>> td {
  font-family: "kumbhMedium" !important;
  font-size: 14px !important;
}

.border-left-4 {
    border-left: 4px solid #005f32;
}
</style>
